import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { conditionRulesApi } from "../../services/api";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input, Textarea, Select } from "../../components/ui/Input";
import { auditApi } from "../../services/api";
import type {
  ConditionRule,
  ConditionRuleUpdate,
} from "../../types/node_group";
import ValuesListEditor from "../../components/ui/ValuesListEditor";

export default function EditConditionRulePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ruleId = Number(id);

  const [loading, setLoading] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [local, setLocal] = React.useState<ConditionRule | null>(null);

  const apply = (chg: Partial<ConditionRule>) =>
    setLocal(
      (prev) => ({ ...(prev as any), ...(chg as any) }) as ConditionRule,
    );

  const load = React.useCallback(async () => {
    if (!Number.isFinite(ruleId)) return;
    setLoading(true);
    setError(null);
    try {
      const data = await conditionRulesApi.get(ruleId);
      setLocal(data);
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to load condition rule";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [ruleId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!local) return;
    try {
      setSaving(true);
      const payload: ConditionRuleUpdate = {
        name: local.name || "",
        description: local.description || null,
        type: local.type,
        values: local.values || null,
        sql_query: local.sql_query || null,
        db_name: local.db_name || null,
        logical_operator: local.logical_operator,
        use_logical_not: local.use_logical_not,
        user_input_node_id: local.user_input_node_id || null,
      } as any;
      const updated = await conditionRulesApi.update(ruleId, payload);
      setLocal(updated);
      toast.success("Saved");
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to save";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm("Delete this condition rule?")) return;
    try {
      await conditionRulesApi.remove(ruleId);
      toast.success("Deleted");
      navigate("/condition-rules");
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to delete";
      toast.error(String(msg));
    }
  };

  if (loading) {
    return <div className="py-12">Loading…</div>;
  }
  if (error) {
    return (
      <div className="space-y-4 py-12">
        <div className="text-red-600">{error}</div>
        <button className="px-3 py-2 border rounded" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  if (!local) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Edit Condition Rule
          </h1>
          <div className="text-sm text-gray-500">ID: {local.id}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/condition-rules" className="px-3 py-2 border rounded">
            Back
          </Link>
          <button
            className="px-3 py-2 border rounded text-red-600"
            onClick={remove}
          >
            Delete
          </button>
          <button
            className="px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
            onClick={save}
            disabled={saving}
          >
            Save
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded border space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Name"
            type="text"
            value={local.name || ""}
            onChange={(e) => apply({ name: e.target.value })}
            variant="rounded-md"
          />
          <Select
            label="Type"
            value={local.type}
            onChange={(e) => apply({ type: e.target.value })}
            variant="rounded-md"
          >
            <option value="any_input">any_input</option>
            <option value="auto">auto</option>
            <option value="input_in">input_in</option>
            <option value="input_not_in">input_not_in</option>
            <option value="sql">sql</option>
            <option value="daytime">daytime</option>
            <option value="quiet_periods">quiet_periods</option>
            <option value="variable_in">variable_in</option>
            <option value="variable_not_in">variable_not_in</option>
            <option value="variable_is_falsy">variable_is_falsy</option>
            <option value="variable_is_truthy">variable_is_truthy</option>
            <option value="variable_is_null">variable_is_null</option>
            <option value="variable_is_not_null">variable_is_not_null</option>
            <option value="variables_expression">variables_expression</option>
            <option value="entrypoint">entrypoint</option>
            <option value="timer">timer</option>
          </Select>
          <Select
            label="Logical operator"
            value={local.logical_operator || ""}
            onChange={(e) =>
              apply({
                logical_operator: (e.target.value || undefined) as any,
              })
            }
            variant="rounded-md"
          >
            <option value="">AND (default)</option>
            <option value="OR">OR</option>
          </Select>
          <label className="text-sm text-gray-700 flex items-center gap-2">
            Not
            <input
              type="checkbox"
              checked={!!local.use_logical_not}
              onChange={(e) => apply({ use_logical_not: e.target.checked })}
            />
          </label>
        </div>

        <Textarea
          label="Description"
          rows={3}
          value={local.description || ""}
          onChange={(e) => apply({ description: e.target.value })}
          variant="rounded-md"
        />

        {(local.type === "input_in" || local.type === "input_not_in") && (
          <div className="mt-1">
            <ValuesListEditor
              values={
                Array.isArray(local.values)
                  ? (local.values as any[])
                  : Array.isArray((local.values as any)?.values)
                    ? (local.values as any).values || []
                    : []
              }
              onChange={(newValues) => apply({ values: newValues })}
              placeholder="Enter value"
              label="Values"
            />
          </div>
        )}

        {local.type === "variable_in" || local.type === "variable_not_in" ? (
          <div className="space-y-4">
            <Input
              label="Variable name"
              type="text"
              value={(local.values as any)?.var_name || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    var_name: e.target.value,
                    values: (local.values as any)?.values || [],
                  },
                })
              }
              variant="rounded-md"
            />
            <ValuesListEditor
              values={
                Array.isArray((local.values as any)?.values)
                  ? (local.values as any).values || []
                  : []
              }
              onChange={(newValues) =>
                apply({
                  values: { ...(local.values as any), values: newValues },
                })
              }
              placeholder="Enter value"
              label="Allowed values"
            />
          </div>
        ) : null}

        {local.type === "variable_is_falsy" ||
        local.type === "variable_is_truthy" ||
        local.type === "variable_is_null" ||
        local.type === "variable_is_not_null" ? (
          <Input
            label="Variable name"
            type="text"
            value={(local.values as any)?.var_name || ""}
            onChange={(e) =>
              apply({
                values: {
                  ...(local.values as any),
                  var_name: e.target.value,
                },
              })
            }
            variant="rounded-md"
          />
        ) : null}

        {local.type === "variables_expression" ? (
          <Textarea
            label="Expression"
            rows={3}
            value={typeof local.values === "string" ? local.values : ""}
            onChange={(e) => apply({ values: e.target.value })}
            variant="rounded-md"
          />
        ) : null}

        {local.type === "quiet_periods" ? (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Names (comma-separated)"
              type="text"
              value={
                Array.isArray((local.values as any)?.names)
                  ? ((local.values as any).names || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                apply({ values: { ...(local.values as any), names: parts } });
              }}
              variant="rounded-md"
            />
            <Input
              label="Tags (comma-separated)"
              type="text"
              value={
                Array.isArray((local.values as any)?.tags)
                  ? ((local.values as any).tags || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                apply({ values: { ...(local.values as any), tags: parts } });
              }}
              variant="rounded-md"
            />
          </div>
        ) : null}

        {local.type === "daytime" ? (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="From (HH:MM:SS)"
              type="text"
              value={(local.values as any)?.from || ""}
              onChange={(e) =>
                apply({
                  values: { ...(local.values as any), from: e.target.value },
                })
              }
              variant="rounded-md"
            />
            <Input
              label="To (HH:MM:SS)"
              type="text"
              value={(local.values as any)?.to || ""}
              onChange={(e) =>
                apply({
                  values: { ...(local.values as any), to: e.target.value },
                })
              }
              variant="rounded-md"
            />
            <Input
              label="Weekdays (0-6, comma-separated)"
              type="text"
              value={
                Array.isArray((local.values as any)?.weekdays)
                  ? ((local.values as any).weekdays || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((x) => Number(x))
                  .filter((x) => Number.isFinite(x));
                apply({
                  values: { ...(local.values as any), weekdays: parts },
                });
              }}
              variant="rounded-md"
            />
            <Input
              label="Timezone (IANA)"
              type="text"
              value={(local.values as any)?.timezone || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    timezone: e.target.value,
                  },
                })
              }
              variant="rounded-md"
            />
          </div>
        ) : null}

        {local.type === "sql" ? (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="DB name"
              type="text"
              value={local.db_name || ""}
              onChange={(e) => apply({ db_name: e.target.value })}
              variant="rounded-md"
            />
            <Textarea
              label="SQL query"
              rows={3}
              value={local.sql_query || ""}
              onChange={(e) => apply({ sql_query: e.target.value })}
              variant="rounded-md"
              className="col-span-2"
            />
          </div>
        ) : null}

        {local.type === "entrypoint" ? (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Came from nodes (ids)"
              type="text"
              value={
                Array.isArray((local.values as any)?.came_from_nodes_id)
                  ? ((local.values as any).came_from_nodes_id || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((x) => Number(x))
                  .filter((x) => Number.isFinite(x));
                apply({
                  values: {
                    ...(local.values as any),
                    came_from_nodes_id: parts,
                    entrypoint_node_id:
                      (local.values as any)?.entrypoint_node_id || null,
                  },
                });
              }}
              variant="rounded-md"
            />
            <Input
              label="Entrypoint node id"
              type="text"
              value={String((local.values as any)?.entrypoint_node_id || "")}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    entrypoint_node_id: e.target.value
                      ? Number(e.target.value)
                      : null,
                  },
                })
              }
              variant="rounded-md"
            />
          </div>
        ) : null}

        {local.type === "timer" ? (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Variable to check (path)"
              type="text"
              value={(local.values as any)?.variable_to_check || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    variable_to_check: e.target.value,
                  },
                })
              }
              variant="rounded-md"
            />
            <Input
              label="Trigger seconds"
              type="text"
              value={String((local.values as any)?.trigger_seconds ?? "")}
              onChange={(e) => {
                const v = e.target.value;
                const num = Number(v);
                apply({
                  values: {
                    ...(local.values as any),
                    trigger_seconds: Number.isFinite(num) ? num : v,
                  },
                });
              }}
              variant="rounded-md"
            />
          </div>
        ) : null}
      </div>
      {local?.id ? (
        <AuditInfo
          resource="condition-rules"
          id={local.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
